import {Component, computed, DestroyRef, effect, inject, Injector, OnInit, signal} from "@angular/core";
import {BasicMaterialDialog, MatDialogDataBaseInput} from "../../basic-material-dialog/basic-material-dialog.directive";
import {ExamFlowScheduleMode, ExamScheduleEntry, ExamStation, Permissions, SimUser} from "@models";
import {Translations} from "@translations";
import {NotificationService} from "@services";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {StudentColorWrapper} from "../../../../web-manager/subModules/exam-schedule/pages/exam-schedule-page/exam-schedule-page.component";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";
import {distinctUntilChanged, filter, map, tap} from "rxjs/operators";
import {slideInLeftOnEnterAnimation, zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {ExamSchedulePageStore} from "../../../../web-manager/subModules/exam-schedule/store/exam-schedule-page.store";

export interface DialogData_AssignStudentToStationDialogInput extends MatDialogDataBaseInput {
	students: StudentColorWrapper[];
}

type StationOptionWrapper = {station: ExamStation; index: number};

@Component({
	selector: "sim-assign-student-to-station-dialog",
	templateUrl: "./assign-student-to-station-dialog.component.html",
	styleUrl: "./assign-student-to-station-dialog.component.scss",
	animations: [
		zoomInOnEnterAnimation({duration: 250}),
		zoomOutOnLeaveAnimation({duration: 250}),
		slideInLeftOnEnterAnimation({duration: 250}),
	],
	standalone: false,
})
export class AssignStudentToStationDialogComponent
	extends BasicMaterialDialog<AssignStudentToStationDialogComponent, DialogData_AssignStudentToStationDialogInput>
	implements OnInit
{
	// Constants
	public readonly T = Translations;
	protected readonly Permissions = Permissions;

	// Properties
	protected readonly hasChanged = signal<boolean>(false);
	protected readonly savePromise = signal<Promise<unknown> | null>(null);

	// Form
	protected readonly form = new FormGroup<{
		runId: FormControl<string | null>;
		student: FormControl<StudentColorWrapper | null>;
		stationId: FormControl<string | null>;
	}>({
		runId: new FormControl<string | null>(null, Validators.required),
		student: new FormControl<StudentColorWrapper | null>(null, Validators.required),
		stationId: new FormControl<string | null>(null, Validators.required),
	});

	// Derived properties
	protected readonly selectedRunId = toSignal<string | null>(this.form.controls.runId.valueChanges);
	protected readonly stations = computed<ExamStation[]>(() => this.store.session()?.examStations ?? []);
	protected readonly studentToStationMap = computed<Map<string, StationOptionWrapper>>(() =>
		this.computeStudentToStationMap(this.store.scheduleModel(), this.selectedRunId(), this.stations()),
	);
	protected readonly stationToStudentMap = computed<Map<string, SimUser | undefined>>(() =>
		this.computeStationToStudentMap(this.store.scheduleModel(), this.selectedRunId(), this.data.students),
	);
	protected readonly selectedStudent = toSignal<StudentColorWrapper | null>(this.form.controls.student.valueChanges);
	protected readonly selectedStation = toSignal<{station: ExamStation; index: number} | null>(
		this.form.controls.stationId.valueChanges.pipe(
			map((id) => {
				if (!id) return null;
				const index = this.stations().findIndex((station) => station.id === id);
				const station = this.stations()[index];
				return station ? {station, index} : null;
			}),
		),
	);
	protected readonly studentsView = computed<StudentColorWrapper[]>(() =>
		this.data.students.toSorted((studentA, studentB) => {
			const stationIndexA = this.studentToStationMap().get(studentA.id)?.index ?? 0;
			const stationIndexB = this.studentToStationMap().get(studentB.id)?.index ?? 0;
			return stationIndexA - stationIndexB;
		}),
	);

	// Injections
	readonly store = inject(ExamSchedulePageStore);
	readonly #destroyRef = inject(DestroyRef);
	readonly #notificationService = inject(NotificationService);

	// Effects
	readonly #handleFirstRunScheduleMode = effect(() => {
		const scheduleMode = this.store.scheduleMode();
		if (scheduleMode === ExamFlowScheduleMode.FirstRunSchedule) {
			this.form.controls.runId.setValue(this.store.runIds().at(0)!);
			this.form.controls.runId.disable();
		}
	});

	public constructor(_injector: Injector) {
		super(_injector);
	}

	public ngOnInit() {
		this.form.valueChanges
			.pipe(
				takeUntilDestroyed(this.#destroyRef),
				tap(this.validateIllegalAssignmentError()),
				filter(() => this.form.valid),
				distinctUntilChanged(
					(valueA, valueB) =>
						valueA.runId === valueB.runId && valueA.student!.id === valueB.student!.id && valueA.stationId === valueB.stationId,
				),
			)
			.subscribe(() => this.hasChanged.set(true));
	}

	/**
	 * Check if user is trying to reassign a student to their current station
	 * @private
	 */
	private validateIllegalAssignmentError() {
		return (
			value: Partial<{
				runId: string | null;
				student: StudentColorWrapper | null;
				stationId: string | null;
			}>,
		) => {
			// Validate
			if (Object.values(value).some((x) => !x)) {
				return;
			}

			const stationStudentId = this.stationToStudentMap().get(value.stationId!)?.id;
			const studentStationId = this.studentToStationMap().get(value.student!.id)?.station?.id;

			// WAITING ROOM
			if (!studentStationId) {
				this.form.controls.student.setErrors(null);
				this.form.controls.stationId.setErrors(null);
				return;
			}

			const illegalAssignment = stationStudentId === value.student!.id && studentStationId === value.stationId;

			if (illegalAssignment) {
				this.form.controls.student.setErrors({illegalAssignment});
				this.form.controls.stationId.setErrors({illegalAssignment});
			} else {
				this.form.controls.student.setErrors(null);
				this.form.controls.stationId.setErrors(null);
			}
		};
	}

	protected async submit() {
		if (!this.hasChanged() || this.form.invalid) {
			return;
		}
		const {runId, stationId, student} = this.form.getRawValue();
		const fromStationId = this.studentToStationMap().get(student!.id)?.station?.id;
		const fromStudentId = this.stationToStudentMap().get(stationId!)?.id;

		const promise = this.store
			.scheduleUpdated({
				runId,
				from: {
					stationId: fromStationId ?? "",
					isInBreak: false,
					studentId: student?.id ?? "",
				},
				to: {
					stationId: stationId ?? "",
					isInBreak: false,
					studentId: fromStudentId ?? "",
				},
			})
			.catch(() => {
				this.#notificationService.notifyError("Something went wrong");
				this.hasChanged.set(true);
			});
		this.hasChanged.set(false);
		this.savePromise.set(promise);
	}

	private computeStudentToStationMap(
		scheduleEntries: ExamScheduleEntry[],
		selectedRunId: string | null | undefined,
		stations: ExamStation[],
	): Map<string, StationOptionWrapper> {
		const relevantEntry = scheduleEntries.find((entry) => entry.id === selectedRunId);
		if (!selectedRunId || !relevantEntry) {
			return new Map<string, StationOptionWrapper>();
		}
		return new Map<string, StationOptionWrapper>(
			relevantEntry.studentToStationMapping.map((mapping) => {
				const stationIndex = stations.findIndex((station) => station.id === mapping.stationId);
				const station = stations[stationIndex];
				return [mapping.studentId, {station, index: stationIndex}];
			}),
		);
	}

	private computeStationToStudentMap(
		scheduleEntries: ExamScheduleEntry[],
		selectedRunId: string | null | undefined,
		students: SimUser[],
	): Map<string, SimUser | undefined> {
		const relevantEntry = scheduleEntries.find((entry) => entry.id === selectedRunId);
		if (!selectedRunId || !relevantEntry) {
			return new Map<string, SimUser | undefined>();
		}
		return new Map<string, SimUser | undefined>(
			relevantEntry.studentToStationMapping.map((mapping) => {
				const relevantStudent = students.find((student) => student.id === mapping.studentId);
				return [mapping.stationId, relevantStudent];
			}),
		);
	}
}
